// loader-interceptor.service.ts
import { Injectable } from "@angular/core";
import {
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { LoadingService } from "./loading.service";
import { catchError, map } from "rxjs/operators";

@Injectable()
export class LoadingInterceptor implements HttpInterceptor {
  private requests: HttpRequest<any>[] = [];

  // URLs that should not trigger loading spinner
  private readonly skipLoadingUrls: string[] = [
    "/api/Cards/Complete",
    "api/Settings/Get",
    // plan-home widgets render their own skeleton/loading states — the global
    // overlay would just hide them and block progressive rendering.
    "/api/PatientPlan/GetPatientPlanFavourites",
    "/api/PatientPlan/GetPatientPlansWithScore",
    // Add URLs that should not show loading spinner
    // Example: '/api/health', '/api/status'
  ];

  // URL patterns that should not trigger loading spinner
  private readonly skipLoadingPatterns: RegExp[] = [
    /\/api\/Settings\/AddOrUpdateUserSetting/i,
    /\/api\/PatientPlan\/GetPatientPlanDetails/i,
    /\/api\/PatientPlan\/GetPatientPlanFavouriteDetails/i,
    // FLIP-326: the app-open ping runs on every tab focus and must never flash the overlay.
    /\/api\/activity\/events/i,
    // Add regex patterns for URLs that should not show loading spinner
    // Example: /\/api\/health\/.*/ for all health check endpoints
    // /\/api\/cards\/.*/i,
  ];

  constructor(private loadingService: LoadingService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Check if this request should skip loading
    if (this.shouldSkipLoading(request)) {
      return next.handle(request);
    }

    this.loadingService.setLoading(request.url);

    return next.handle(request).pipe(
      catchError((err) => {
        this.loadingService.removeLoading(request.url);
        return throwError(() => err);
      }),
      map<HttpEvent<any>, any>((evt: HttpEvent<any>) => {
        if (evt instanceof HttpResponse) {
          this.loadingService.removeLoading(request.url);
        }
        return evt;
      })
    );
  }

  /**
   * Determines if the loading spinner should be skipped for this request
   * @param request The HTTP request
   * @returns true if loading should be skipped, false otherwise
   */
  private shouldSkipLoading(request: HttpRequest<any>): boolean {
    // Skip if URL is in the skip list
    if (this.skipLoadingUrls.some((url) => request.url.includes(url))) {
      return true;
    }

    // Skip if URL matches any of the skip patterns
    if (this.skipLoadingPatterns.some((pattern) => pattern.test(request.url))) {
      return true;
    }

    return false;
  }
}
